import { AxiosError, AxiosResponse } from 'axios';
import { FormikErrors, FormikValues } from 'formik';
import { set } from 'lodash';
import { Renderable, toast } from 'react-hot-toast';

export interface CompliOneApiError {
  error_code: number;
  error_detail: string;
  error_message: string;
  validation_errors: ValidationErrorDetailItem[];
}

export interface ValidationErrorDetailItem {
  loc: string[];
  msg: string;
  type: string;
}

export function formatApiErrorMessage(error: AxiosError<CompliOneApiError>): string {
  return error.response?.data?.error_message || error.message || 'Unknown error';
}

export function extractValidationErrors(
  error: AxiosError<CompliOneApiError>,
): FormikErrors<FormikValues> {
  if (error.response?.status === 400 && error.response?.data?.validation_errors) {
    return error.response?.data?.validation_errors.reduce((acc, item) => {
      const key = item.loc.filter(node => node !== 'body').join('.');
      set(acc, key, item.msg);
      return acc;
    }, {} as FormikErrors<FormikValues>);
  }
  return {};
}

export async function withErrorHandling<T>(
  fn: Promise<AxiosResponse<T>>,
  params: {
    loading: string | Renderable;
    success: string | Renderable;
    setErrors?: (errors: FormikErrors<FormikValues>) => void;
    notificationDuration?: number;
    errorDuration?: number;
    customErrorMessage?: string;
  } = {
    loading: 'Loading...',
    success: 'Success!',
    notificationDuration: 2500,
    errorDuration: 5000,
    customErrorMessage: undefined,
  },
): Promise<AxiosResponse<T>> {
  const toastId = toast.loading(params.loading);
  try {
    const response = await fn;

    if (params.success) {
      toast.success(params.success, {
        id: toastId,
        duration: params.notificationDuration ?? 2500,
      });
    } else {
      toast.dismiss(toastId);
    }

    return response;
  } catch (e) {
    const error = e as AxiosError<CompliOneApiError>;

    let errorMessage = formatApiErrorMessage(error);

    if (error.response?.status === 400) {
      if (params?.customErrorMessage) {
        errorMessage = params.customErrorMessage;
      } else if (error.response?.data?.validation_errors && params.setErrors) {
        params.setErrors?.(extractValidationErrors(error));
        errorMessage = 'Validation error occurred, check your input data.';
      }
    }

    if (error.response?.status === 409 || error.response?.status === 401) {
      errorMessage = formatApiErrorMessage(error) || 'An account with this email already exists.';
    }

    toast.error(errorMessage, {
      id: toastId,
      duration: params.errorDuration ?? params.notificationDuration ?? 2500,
    });

    throw error;
  }
}
